import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Steps } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { StepsProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { AgGridDataRow, IStep, IStepButtonItem, IStepButtonOptions } from 'core/types';
import { useApplySelectedSpaToLines } from 'quotes/components/QuotePartSelectSpa';
import { QuoteEntityType, QuoteType, QuoteVersionType } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { useQuotePartActions, useRefreshQuotePartRows } from 'quotes/hooks';
import { mapUpsertQuotePartLine } from 'quotes/mappers';
import { ICustomerQuote, ICustomerQuotePart, IQuotePart, IQuotePartLineForm } from 'quotes/types';
import { getActiveQuoteParts, getSelectedSpaPick } from 'quotes/utils';
import { UpsertTaxonomyGroup } from 'taxonomy/components';
import { TaxonomySource } from 'taxonomy/enums';
import { ITaxonomyPath } from 'taxonomy/types';
import { Color, Size } from 'theme/enums';

import {
  findNextEditableLine,
  IsLineEditable,
  resolveLineAfterSave,
  scrollLineIntoView,
} from './nextLine';
import { LineDetails } from './UpsertLine';
import { useNextEditableLine } from './useNextEditableLine';

interface IProps {
  quoteId: number;
  quoteType?: QuoteType;
  quotePart: IQuotePart;
  alternate: boolean;
  isEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  quote: ICustomerQuote;
  isAlternateVersionEnabled?: boolean;
  entityType?: QuoteEntityType;
  quoteVersion?: QuoteVersionType;
  // Both given by the hosts that open Edit Line from the grid; without them there is no Next Line.
  isLineEditable?: IsLineEditable;
  onOpenLine?: (quotePart: IQuotePart) => void;
}

enum SubmitType {
  SAVE = 'save',
  SAVE_AND_NEW = 'saveAndNew',
  UPDATE = 'update',
}

export const UpsertLineSteps = ({
  quoteId,
  quoteType = QuoteType.CUSTOMER,
  quotePart,
  alternate = false,
  isEdit = false,
  isOpen,
  onClose,
  onSubmitSuccess,
  quote,
  isAlternateVersionEnabled = false,
  entityType,
  quoteVersion,
  isLineEditable,
  onOpenLine,
}: IProps) => {
  const { t } = useTranslation();

  const [stepsComponentKey, setStepsComponentKey] = useState(Math.random());
  const [formValues, setFormValues] = useState<IQuotePartLineForm>();
  const [hasExternalTaxonomy, setHasExternalTaxonomy] = useState<boolean>(false);
  const [validatePartTaxonomyPath, setValidatePartTaxonomyPath] = useState<ITaxonomyPath[]>(null);
  // Save and close wait for the form whatever the notification slot shows.
  const [isLineValidating, setIsLineValidating] = useState(false);

  const lineDetailsFormRef = useRef(null);
  const taxonomyFormRef = useRef(null);

  const { clearDrawerNotification, clearFormNotification, showNotification } =
    useNotificationContext();
  const { gridApi, rows, selectedRows, replaceRow, updateRows } = useAgGridClientTableContext();
  const refreshQuotePartRows = useRefreshQuotePartRows(quoteId, quoteVersion);

  const hasNextLineButton = isEdit && Boolean(isLineEditable && onOpenLine);
  const nextLine = useNextEditableLine({
    gridApi,
    currentLineId: quotePart?.id,
    isLineEditable,
    enabled: isOpen && hasNextLineButton,
  });

  // Opened from the grid, by Edit Line or Next Line: the grid keeps that line in view.
  useEffect(() => {
    if (isOpen && hasNextLineButton) {
      scrollLineIntoView(gridApi, quotePart?.id);
    }
  }, [gridApi, hasNextLineButton, isOpen, quotePart?.id]);

  const { user } = useAuthUser();
  const { changeQuotePart, upsertQuotePart } = useQuotePartActions({
    notificationPosition: NotificationPosition.DRAWER,
  });

  useEffect(() => {
    if (!isOpen) {
      setHasExternalTaxonomy(false);
    }
  }, [isOpen]);

  const isTaxonomyRequired = useMemo(
    () => QuoteType.CUSTOMER === quoteType && Boolean(user.companySettings.taxonomyRequired),
    [quoteType, user.companySettings.taxonomyRequired]
  );

  const isTaxonomyStepEnabled = useMemo(
    () =>
      isAlternateVersionEnabled &&
      QuoteType.CUSTOMER === quoteType &&
      user.companySettings.taxonomyEnabled &&
      !hasExternalTaxonomy,
    [
      hasExternalTaxonomy,
      isAlternateVersionEnabled,
      quoteType,
      user.companySettings.taxonomyEnabled,
    ]
  );

  useEffect(() => {
    setHasExternalTaxonomy(TaxonomySource.EXTERNAL === quotePart?.taxonomySource);
  }, [quotePart]);

  // The persisted state, not the form's: it decides the explicit removal flag and the prompt.
  const previousSelectedSpa = useMemo(
    () => getSelectedSpaPick(quotePart as ICustomerQuotePart),
    [quotePart]
  );
  const isSelectedSpaLine = null !== previousSelectedSpa;
  const savedQuotePart = isEdit ? (quotePart as ICustomerQuotePart) : null;

  const { promptApplyToOtherLines, applyToLinesConfirmation } = useApplySelectedSpaToLines({
    quoteId,
    onApplied: onSubmitSuccess,
  });

  const onLineDetailsSubmit = useCallback(async () => {
    try {
      const formResult = await lineDetailsFormRef.current.triggerFormSubmit();

      setFormValues({ ...formValues, ...formResult });

      return true;
    } catch {
      return false;
    }
  }, [formValues]);

  const getFormData = async () => {
    let data = { ...formValues };

    if (isTaxonomyStepEnabled) {
      const taxonomyFields = await taxonomyFormRef.current.triggerFormSubmit();

      data = { ...data, ...taxonomyFields };

      setFormValues(data);
    } else {
      const lineDetailsFields = await lineDetailsFormRef.current.triggerFormSubmit();

      data = {
        ...data,
        ...lineDetailsFields,
      };

      setFormValues(data);
    }

    return data;
  };

  const onSubmitAddNew = async (type = SubmitType.SAVE) => {
    let data = null;

    try {
      data = await getFormData();
    } catch {
      return false;
    }

    let masterQuotePartId = null;
    if (alternate) {
      masterQuotePartId = selectedRows[0].id;
    }

    const upsertPayload = mapUpsertQuotePartLine(
      quoteId,
      quoteType,
      data,
      quotePart?.id,
      null,
      masterQuotePartId,
      {
        isAlternateVersionEnabled,
        isTaxonomyStepEnabled,
        isTaxonomyRequired,
        hasExternalTaxonomy,
        isSelectedSpaLine,
      }
    );

    let upsertResult = null;

    try {
      upsertResult = await upsertQuotePart(upsertPayload);
    } catch (error) {
      // Request errors (e.g. 422 validation) are notified by useHandleErrors; keep the drawer open.
      console.error(error);

      return false;
    }

    const { affectedQuotePartIds, success, quotePart: updatedQuotePart } = upsertResult ?? {};

    if (!success) {
      return false;
    }

    const addIndex = getActiveQuoteParts(rows as IQuotePart[]).length;

    const transaction = { add: [updatedQuotePart], addIndex };
    const transactionOptions = {};
    if (alternate) {
      const masterQuotePart = gridApi.getRowNode(masterQuotePartId);

      const alternateParts =
        rows.filter((row: AgGridDataRow) => row.masterQuotePartId === masterQuotePartId) || [];

      transaction['addIndex'] = masterQuotePart.rowIndex + alternateParts.length + 1;
      transaction['update'] = [{ ...masterQuotePart.data, substituted: 1 }];
      transactionOptions['redrawRows'] = true;
    }

    updateRows(transaction, transactionOptions);
    refreshQuotePartRows(affectedQuotePartIds);

    onSubmitSuccess && onSubmitSuccess();

    // The prompt lives outside the drawer, which auto-closes right after this submit.
    promptApplyToOtherLines({
      savedQuotePart: updatedQuotePart,
      previousSelectedSpa,
    });

    if (SubmitType.SAVE_AND_NEW === type) {
      setFormValues(null);
      setStepsComponentKey(Math.random());

      return true;
    }

    return true;
  };

  // A rejection without an error is the form's own validation, which the form shows itself.
  const notifyBlockedSave = (error?: unknown) => {
    if (!error) {
      return;
    }

    console.error(error);
    showNotification({
      title: t('titleError'),
      message: t('messageLineNotSaved'),
      type: ApiStatus.ERROR,
      position: NotificationPosition.DRAWER,
    });
  };

  const onSubmitUpdate = async () => {
    let data = null;
    let changePayload = null;

    try {
      data = await getFormData();

      changePayload = mapUpsertQuotePartLine(
        quoteId,
        quoteType,
        data,
        quotePart?.id,
        quotePart?.quotePriceDecimals,
        null,
        {
          isAlternateVersionEnabled,
          isTaxonomyStepEnabled,
          isTaxonomyRequired,
          hasExternalTaxonomy,
          isSelectedSpaLine,
          savedQuotePart,
        }
      );
    } catch (error) {
      notifyBlockedSave(error);

      return false;
    }

    let changeResult = null;

    try {
      changeResult = await changeQuotePart(changePayload);
    } catch (error) {
      // Request errors (e.g. "Part has been already added" 422) are notified by useHandleErrors;
      // keep the drawer open so the user can adjust the part or cancel.
      console.error(error);

      return false;
    }

    const { affectedQuotePartIds, success, quotePart: updatedQuotePart } = changeResult ?? {};

    if (!success) {
      return false;
    }

    const agGridDataRow = { ...data, id: quotePart?.id } as AgGridDataRow;

    replaceRow(agGridDataRow, updatedQuotePart);
    refreshQuotePartRows(affectedQuotePartIds);

    onSubmitSuccess && onSubmitSuccess();

    // The prompt lives outside the drawer, which auto-closes right after this submit.
    promptApplyToOtherLines({
      savedQuotePart: updatedQuotePart,
      previousSelectedSpa,
    });

    return true;
  };

  // Update Line's save, then the drawer stays open on the next line the grid shows.
  const onSubmitNextLine = async () => {
    const plannedLine = findNextEditableLine(gridApi, quotePart?.id, isLineEditable);

    if (!plannedLine) {
      return false;
    }

    const lineDetailsForm = lineDetailsFormRef.current;

    if (!isTaxonomyStepEnabled && !lineDetailsForm) {
      notifyBlockedSave(new Error('Next Line: the line form is not mounted.'));

      return false;
    }

    const plannedRowIndex = gridApi.getRowNode(String(plannedLine.id))?.rowIndex;

    // The taxonomy step's own fields are not in the check: that variant always saves.
    const hasUnsavedChanges = isTaxonomyStepEnabled || false !== lineDetailsForm.hasChanges?.();

    if (hasUnsavedChanges && !(await onSubmitUpdate())) {
      return false;
    }

    const lineToOpen = hasUnsavedChanges
      ? resolveLineAfterSave(
          gridApi,
          plannedLine.id,
          plannedRowIndex,
          quotePart?.id,
          isLineEditable
        )
      : plannedLine;

    if (!lineToOpen) {
      // Saved, but the grid no longer shows a line after it: finish as Update Line does.
      return true;
    }

    setFormValues(null);
    setValidatePartTaxonomyPath(null);
    onOpenLine(lineToOpen);
    setStepsComponentKey(Math.random());

    return 'details';
  };

  const handleOnClose = () => {
    // Scoped: a blanket clear would wipe the TOP notification of an apply-to-lines job.
    clearFormNotification();
    clearDrawerNotification();
    onClose();
    setFormValues(null);
  };

  // Shift+Enter inside the line details form fires exactly these options (see submitFormOnEnter),
  // so keep the two paths sharing one object.
  const saveAndAddNextOptions: IStepButtonOptions = {
    onClick: () => onSubmitAddNew(SubmitType.SAVE_AND_NEW),
    stepType: 'details',
    disabled: isLineValidating,
  };

  const submitButtons: IStepButtonItem[] = [
    {
      text: t('buttonLabelSaveAndAddNext'),
      hide: isEdit,
      options: saveAndAddNextOptions,
    },
    {
      text: t('buttonLabelSaveAndClose'),
      hide: isEdit,
      options: {
        onClick: () => onSubmitAddNew(SubmitType.SAVE),
        disabled: isLineValidating,
      },
    },
    {
      text: t('buttonLabelUpdateLine'),
      hide: !isEdit,
      options: { onClick: () => onSubmitUpdate(), disabled: isLineValidating },
    },
    {
      text: t('buttonLabelNextLine'),
      hide: !hasNextLineButton,
      options: { onClick: onSubmitNextLine, disabled: isLineValidating || !nextLine },
    },
  ];

  const steps: IStep[] = [
    {
      type: 'details',
      title: t('titleLineDetails'),
      content: (
        <LineDetails
          ref={lineDetailsFormRef}
          quoteId={quoteId}
          quoteType={quoteType}
          quotePart={quotePart}
          isEdit={isEdit}
          isOpen
          onClose={onClose}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          entityType={entityType}
          quote={quote}
          setHasExternalTaxonomy={setHasExternalTaxonomy}
          setValidatePartTaxonomyPath={setValidatePartTaxonomyPath as any}
          defaultValues={formValues}
          onSubmit={
            isTaxonomyStepEnabled ? onLineDetailsSubmit : isEdit ? onSubmitUpdate : onSubmitAddNew
          }
          // Only offered while Save & Add Next actually sits on this step: edit mode hides it, and
          // the taxonomy variant moves it to the taxonomy step (whose form isn't even mounted yet),
          // leaving the details step its plain Next for both Enter and Shift+Enter.
          saveAndAddNextOptions={!isEdit && !isTaxonomyStepEnabled ? saveAndAddNextOptions : null}
          onValidatingChange={setIsLineValidating}
        />
      ),
      nextButtons: submitButtons,
      cancelButton: { options: { disabled: isLineValidating } },
    },
  ];

  if (isTaxonomyStepEnabled) {
    steps[0] = {
      ...steps[0],
      nextButton: {
        options: { onClick: onLineDetailsSubmit, disabled: isLineValidating },
      },
      nextButtons: null,
    };

    steps.push({
      type: 'taxonomy',
      title: t('titleTaxonomy'),
      content: (
        <UpsertTaxonomyGroup
          ref={taxonomyFormRef}
          defaultTaxonomyPath={quotePart?.taxonomyPath || validatePartTaxonomyPath}
        />
      ),
      nextButtons: submitButtons,
    });
  }

  return (
    <>
      <StepsProvider steps={steps} onClose={handleOnClose}>
        <Steps
          key={stepsComponentKey}
          title={getFormTitle(isEdit, alternate)}
          subtitle={isEdit ? getLinePartNumber(quotePart) : undefined}
          isOpen={isOpen}
          color={Color.NEUTRAL}
          size={Size.MD}
          drawerSlotAndSlotProps={
            isAlternateVersionEnabled
              ? { slotProps: { content: { sx: { maxWidth: '600px', width: '600px' } } } }
              : undefined
          }
          fullWidth={false}
          interactive
          disableClose={isLineValidating}
        />
      </StepsProvider>

      {/* Outside <Steps>, which unmounts its content ~1s after a successful submit. */}
      {applyToLinesConfirmation}
    </>
  );
};

const getFormTitle = (isEdit: boolean, alternate: boolean) => {
  if (isEdit) {
    return t('titleUpdateLine');
  }

  if (alternate) {
    return t('titleAddAlternateLine');
  }

  return t('titleAddLine');
};

// A generic part's placeholder dash is no part number to show.
const getLinePartNumber = (quotePart: IQuotePart) => {
  const partNumber = quotePart ? QuotePartHelper.getDisplayedPartNumber(quotePart) : null;

  return partNumber && DEFAULT_PLACEHOLDER !== partNumber ? partNumber : undefined;
};
